/**
 * @file Renders the modal that shows an import's progress and errors.
 */
import React, { useState } from "react";
import { Progress, Space } from "antd";

import { MODAL_LARGE_HEIGHT, MODAL_LARGE_WIDTH, W3, W0, W1 } from "@components/Constants";

import Modal from "../Modal";
import type { ModalRef } from "../types";
import ImportModalErrors from "./ImportModalErrors";
import type { ImportError } from "./types";

const LABEL_ERRORS = "Errors";
const LABEL_PREFIX = "Row ";

type Props = {
  errors?: ImportError[];
  onClose?: () => void;
  progress?: number;
  errorTitle?: string;
  errorPrefix?: string;
  modal: ModalRef;
  title: string;
  header: React.ReactNode;
  children: React.ReactNode;
};

/**
 * Renders a modal that shows an import's header, progress bar, row errors
 * and children.
 */
const ImportModal = ({
  errors = undefined,
  onClose = undefined,
  progress = undefined,
  errorTitle = LABEL_ERRORS,
  errorPrefix = LABEL_PREFIX,
  modal,
  title,
  header,
  children,
}: Props): JSX.Element => {
  const [ isOpen, setIsOpen ] = useState(false);

  /**
   * Runs the custom close handler and hides the modal.
   */
  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    setIsOpen(false);
  };

  /**
   * Opens the modal.
   */
  modal.current.open = () =>
    setIsOpen(true);

  /**
   * Closes the modal.
   */
  modal.current.close = () =>
    handleClose();

  const modalProps = {
    width: MODAL_LARGE_WIDTH,
    onClose: handleClose,
    destroyOnClose: true,
    title,
    isOpen,
  };

  return (
    <Modal {...modalProps}>
      <div style={{ minHeight: MODAL_LARGE_HEIGHT }}>
        <Space
          style={{ width: "100%" }}
          direction="vertical"
        >
          <Space
            align="start"
            className="import-modal-form"
            direction="horizontal"
            style={{
              display: "flex",
              minHeight: W3 - W0,
              justifyContent: "space-between",
            }}
          >
            {header}
          </Space>

          {
            !!progress && (
              <Progress percent={progress} />
            )
          }

          <div style={{ marginTop: W0 }}>
            {
              errors && (
                <div style={{ marginBottom: W1 }}>
                  <ImportModalErrors
                    title={errorTitle}
                    errors={errors}
                    prefix={errorPrefix}
                  />
                </div>
              )
            }

            {children}
          </div>
        </Space>
      </div>
    </Modal>
  );
};

export default ImportModal;
